import { useEffect, useState } from "react";
import {
  CornerUpLeft,
  MoreHorizontal,
  Pencil,
  Trash2,
  Flag,
  Ban,
  Check,
  X,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { Avatar } from "../ui";
import MentionText from "./MentionText";
import MediaGrid from "./MediaGrid";
import QuotedReplyChip from "./QuotedReplyChip";
import { discussionsApi, formatMessageTime, formatDateTime } from "./api";

/**
 * One message in the timeline. Body is rendered via <MentionText> (escaped,
 * no raw HTML). Hover reveals Reply, plus author Edit/Delete and (for any
 * resident) Report; moderators can Delete others' messages.
 */
export default function MessageBubble({
  message,
  mine,
  canModerate,
  highlighted,
  onReply,
  onJumpToQuote,
  onEdit,
  onDelete,
  onReport,
  onBlock,
  onRetry,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body || "");

  // Local reaction state, seeded from the message and re-synced whenever the
  // server-sent counts change (e.g. on history load). Click is optimistic and
  // reconciled with the endpoint's authoritative counts.
  const [rx, setRx] = useState({
    like: message.like_count || 0,
    dislike: message.dislike_count || 0,
    mine: message.my_reaction || null,
  });
  useEffect(() => {
    setRx({
      like: message.like_count || 0,
      dislike: message.dislike_count || 0,
      mine: message.my_reaction || null,
    });
  }, [message.like_count, message.dislike_count, message.my_reaction]);

  const react = async (value) => {
    const prev = rx;
    // Optimistic: toggling the active one clears it; otherwise switch.
    const next = { ...prev };
    const turningOff = prev.mine === value;
    if (prev.mine === "like") next.like -= 1;
    if (prev.mine === "dislike") next.dislike -= 1;
    if (!turningOff) {
      if (value === "like") next.like += 1;
      else next.dislike += 1;
    }
    next.mine = turningOff ? null : value;
    setRx(next);
    try {
      const res = await discussionsApi.reactToMessage(message.id, next.mine);
      setRx({
        like: res.like_count,
        dislike: res.dislike_count,
        mine: res.my_reaction,
      });
    } catch {
      setRx(prev); // roll back on failure
    }
  };

  const deleted = message.deleted;
  const blocked = message.blocked;
  const pending = message._pending;
  const failed = message._failed;
  const author = message.author || {};

  const startEdit = () => {
    setDraft(message.body || "");
    setEditing(true);
    setMenuOpen(false);
  };

  const saveEdit = async () => {
    const next = draft.trim();
    if (!next || next === message.body) {
      setEditing(false);
      return;
    }
    const ok = await onEdit(message, next);
    if (ok !== false) setEditing(false);
  };

  if (deleted || blocked) {
    return (
      <div className="vdsc-msg deleted" data-mid={message.id}>
        <div className="vdsc-msg-avatar">
          <Avatar name={author.name} imageUrl={author.avatar_url} size={32} />
        </div>
        <div className="vdsc-msg-main">
          <div className="vdsc-msg-head">
            <span className="vdsc-msg-author">{author.name || "Unknown member"}</span>
          </div>
          <div className="vdsc-msg-tombstone">
            {blocked
              ? "You blocked this member"
              : message.deleted_reason === "MODERATOR"
                ? "Message removed by a moderator"
                : "Message deleted"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`vdsc-msg ${mine ? "mine" : ""} ${highlighted ? "highlight" : ""}`}
      data-mid={message.id}
    >
      <div className="vdsc-msg-avatar">
        <Avatar name={author.name} imageUrl={author.avatar_url} size={32} />
      </div>
      <div className="vdsc-msg-main">
        <div className="vdsc-msg-head">
          <span className="vdsc-msg-author">{author.name || "Unknown member"}</span>
          <span className="vdsc-msg-time" title={formatDateTime(message.created_at)}>
            {formatMessageTime(message.created_at)}
          </span>
          {message.edited_at && <span className="vdsc-msg-edited">(edited)</span>}
          {pending && <span className="vdsc-msg-status">sending…</span>}
          {failed && (
            <button
              type="button"
              className="vdsc-msg-status failed"
              onClick={() => onRetry && onRetry(message)}
            >
              failed — retry
            </button>
          )}
        </div>

        {message.reply_preview && (
          <QuotedReplyChip preview={message.reply_preview} onJump={onJumpToQuote} compact />
        )}

        {editing ? (
          <div className="vdsc-msg-editor">
            <textarea
              className="vdsc-textarea"
              rows={2}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  saveEdit();
                }
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <div className="vdsc-msg-editor-actions">
              <button type="button" className="vdsc-iconbtn" onClick={saveEdit} title="Save">
                <Check size={15} />
              </button>
              <button
                type="button"
                className="vdsc-iconbtn"
                onClick={() => setEditing(false)}
                title="Cancel"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        ) : (
          <div className="vdsc-msg-bubble">
            {message.body && (
              <div className="vdsc-msg-body">
                <MentionText body={message.body} mentions={message.mentions} />
              </div>
            )}
            {message.media && message.media.length > 0 && (
              <MediaGrid media={message.media} />
            )}
          </div>
        )}

        {!editing && !pending && (
          <div className="vdsc-msg-reactions">
            <button
              type="button"
              className={`vdsc-react ${rx.mine === "like" ? "active" : ""}`}
              title="Like"
              aria-pressed={rx.mine === "like"}
              onClick={() => react("like")}
            >
              <ThumbsUp size={14} />
              {rx.like > 0 && <span>{rx.like}</span>}
            </button>
            <button
              type="button"
              className={`vdsc-react ${rx.mine === "dislike" ? "active dislike" : ""}`}
              title="Dislike"
              aria-pressed={rx.mine === "dislike"}
              onClick={() => react("dislike")}
            >
              <ThumbsDown size={14} />
              {rx.dislike > 0 && <span>{rx.dislike}</span>}
            </button>
          </div>
        )}
      </div>

      {!editing && !pending && (
        <div className="vdsc-msg-actions">
          <button
            type="button"
            className="vdsc-iconbtn"
            title="Reply"
            onClick={() => onReply(message)}
          >
            <CornerUpLeft size={15} />
          </button>
          <div className="vdsc-msg-menu">
            <button
              type="button"
              className="vdsc-iconbtn"
              title="More"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <MoreHorizontal size={15} />
            </button>
            {menuOpen && (
              <>
                <div
                  className="vdsc-menu-backdrop"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="vdsc-menu" role="menu">
                  {mine && (
                    <button
                      type="button"
                      className="vdsc-menu-item"
                      onClick={startEdit}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                  {(mine || canModerate) && (
                    <button
                      type="button"
                      className="vdsc-menu-item danger"
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete(message);
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                  {!mine && (
                    <button
                      type="button"
                      className="vdsc-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        onReport(message);
                      }}
                    >
                      <Flag size={14} /> Report
                    </button>
                  )}
                  {!mine && author.id && onBlock && (
                    <button
                      type="button"
                      className="vdsc-menu-item danger"
                      onClick={() => {
                        setMenuOpen(false);
                        onBlock(message);
                      }}
                    >
                      <Ban size={14} /> Block member
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
